import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { usePublicCatalog } from "@/hooks/usePublicCatalog";
import CatalogIndexNav from "@/pages/catalog/components/CatalogIndexNav";
import CatalogProductCard from "@/pages/catalog/components/CatalogProductCard";
import CatalogProductModal from "@/pages/catalog/components/CatalogProductModal";
import { buildGroups, buildWhatsappUrl } from "@/pages/catalog/catalogUtils";
import { DEFAULT_LOGO_URL, BRAND_NAME, BRAND_TAGLINE } from "@/lib/constants";
import type { PublicCatalogItem } from "@/types";

const HERO_IMAGE =
  "https://readdy.ai/api/search-image?query=Abstract%20stylized%20close%20up%20of%20heavy%20duty%20truck%20tire%20tread%20patterns%20and%20industrial%20workshop%20textures%20in%20warm%20terracotta%20cream%20and%20amber%20tones%2C%20soft%20studio%20lighting%2C%20minimal%20modern%20graphic%20composition%2C%20high%20detail&width=1600&height=900&seq=public-catalog-hero&orientation=landscape";

function normalize(value: string | null | undefined): string {
  return (value ?? "").toLowerCase();
}

export default function PublicCatalogPage() {
  const { items, config, company, loading, error, reload } = usePublicCatalog();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<PublicCatalogItem | null>(null);
  const [showTop, setShowTop] = useState(false);

  const companyName = company?.company_name || BRAND_NAME;
  const logoUrl = company?.logo_url || DEFAULT_LOGO_URL;
  const whatsappNumber = config.whatsapp_number || company?.whatsapp || null;
  const contactWhatsappUrl = buildWhatsappUrl(whatsappNumber, "Hola, quisiera información sobre los productos del catálogo.");

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((item) => {
      const haystack = [
        item.name,
        item.sku,
        item.tire_size,
        item.model,
        item.brand_name,
        item.category_name,
      ]
        .map(normalize)
        .join(" ");
      return haystack.includes(term);
    });
  }, [items, search]);

  const groups = useMemo(() => buildGroups(filtered), [filtered]);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50 text-foreground-950">
      <header className="sticky top-0 z-40 h-16 border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex h-full w-full max-w-6xl items-center justify-between gap-4 px-4 md:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center">
              <img src={logoUrl} alt={companyName} className="h-full w-full object-contain" />
            </span>
            <div className="min-w-0">
              <p className="truncate font-heading text-sm font-bold text-foreground-950">{companyName}</p>
              <p className="hidden truncate text-[11px] text-foreground-500 sm:block">{BRAND_TAGLINE}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {config.show_whatsapp && contactWhatsappUrl ? (
              <a
                href={contactWhatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-whatsapp-line text-sm"></i>
                Contactar
              </a>
            ) : null}
          </div>
        </div>
      </header>

      <CatalogIndexNav groups={groups} />

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 md:px-6 md:py-8">
        <section className="relative overflow-hidden rounded-xl border border-background-200">
          <img src={HERO_IMAGE} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/94 to-background-50/45" />
          <div className="relative px-5 py-9 md:px-10 md:py-14 lg:max-w-2xl">
            <h1 className="mt-4 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
              Catálogo de Neumaticos y Mas
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
              Índice completo de los productos disponibles. Encuentra rápido por categoría, medida o código y
              consulta el detalle de cada uno.
            </p>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative w-full sm:max-w-sm">
                <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-base"></i>
                </span>
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar producto, medida o código..."
                  className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              {search ? (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                >
                  <i className="ri-close-line text-base"></i>
                  Limpiar
                </button>
              ) : null}
            </div>
          </div>
        </section>

        <div className="mt-6 flex items-center justify-between gap-3">
          <p className="text-sm text-foreground-600">
            <strong className="font-semibold text-foreground-950">{filtered.length}</strong>{" "}
            {filtered.length === 1 ? "producto" : "productos"}
            {search ? " encontrados" : " en catálogo"}
            {groups.length > 0 ? ` · ${groups.length} ${groups.length === 1 ? "categoría" : "categorías"}` : ""}
          </p>
        </div>

        {error ? (
          <div className="mt-4 flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800 sm:flex-row sm:items-center sm:justify-between">
            <span className="flex items-center gap-2">
              <i className="ri-error-warning-line text-base"></i>
              {error}
            </span>
            <button
              type="button"
              onClick={reload}
              className="whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-primary-700 transition-colors hover:bg-background-100"
            >
              Reintentar
            </button>
          </div>
        ) : null}

        {loading ? (
          <div className="mt-4 flex items-center justify-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-sm text-foreground-500">
            <i className="ri-loader-4-line animate-spin text-lg"></i>
            Cargando catálogo...
          </div>
        ) : groups.length === 0 ? (
          <div className="mt-4 flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <p className="text-sm font-semibold text-foreground-800">
              {search ? "Sin resultados" : "Catálogo en preparación"}
            </p>
            <p className="max-w-sm text-xs text-foreground-500">
              {search
                ? "No encontramos productos que coincidan con tu búsqueda. Prueba con otra medida o código."
                : "Aún no hay productos visibles en el catálogo. Vuelve pronto."}
            </p>
          </div>
        ) : (
          <div className="mt-4 flex flex-col gap-8">
            {groups.map((group) => (
              <section key={group.key} id={`catalog-${group.key}`} className="scroll-mt-36">
                <div className="mt-4 h-px w-full bg-background-200" />
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {group.items.map((item) => (
                    <CatalogProductCard
                      key={item.id}
                      item={item}
                      showPrices={config.show_prices}
                      onOpen={setSelected}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

      <footer className="mt-10 border-t border-background-200 bg-background-100">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-6 px-4 py-8 md:grid-cols-3 md:px-6">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center">
                <img src={logoUrl} alt={companyName} className="h-full w-full object-contain" />
              </span>
              <div>
                <p className="font-heading text-sm font-bold text-foreground-950">{companyName}</p>
                <p className="text-[11px] text-foreground-500">{BRAND_TAGLINE}</p>
              </div>
            </div>
            {company?.address ? (
              <p className="flex items-start gap-2 text-xs text-foreground-600">
                <i className="ri-map-pin-line mt-0.5 text-sm text-primary-600"></i>
                <span>{company.address}</span>
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-2">
            <h3 className="font-heading text-sm font-semibold text-foreground-900">Contacto</h3>
            {company?.phone ? (
              <a
                href={`tel:${company.phone}`}
                className="flex items-center gap-2 text-xs text-foreground-600 transition-colors hover:text-primary-700"
              >
                <i className="ri-phone-line text-sm"></i>
                {company.phone}
              </a>
            ) : null}
            {company?.email ? (
              <a
                href={`mailto:${company.email}`}
                className="flex items-center gap-2 text-xs text-foreground-600 transition-colors hover:text-primary-700"
              >
                <i className="ri-mail-line text-sm"></i>
                {company.email}
              </a>
            ) : null}
            {contactWhatsappUrl ? (
              <a
                href={contactWhatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-xs text-foreground-600 transition-colors hover:text-primary-700"
              >
                <i className="ri-whatsapp-line text-sm"></i>
                WhatsApp
              </a>
            ) : null}
          </div>

          <div className="flex flex-col gap-2">
            <h3 className="font-heading text-sm font-semibold text-foreground-900">Navegación</h3>
            <Link
              to="/login"
              className="flex items-center gap-2 text-xs text-foreground-600 transition-colors hover:text-primary-700"
            >
              <i className="ri-lock-line text-sm"></i>
              Acceso interno
            </Link>
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="flex items-center gap-2 text-left text-xs text-foreground-600 transition-colors hover:text-primary-700"
            >
              <i className="ri-arrow-up-line text-sm"></i>
              Volver arriba
            </button>
          </div>
        </div>
        <div className="border-t border-background-200 px-4 py-4 text-center text-[11px] text-foreground-400 md:px-6">
          © {new Date().getFullYear()} {companyName}. Mapa del catálogo de productos.
        </div>
      </footer>

      {showTop ? (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Volver arriba"
          className="fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full border border-background-300 bg-background-50 text-foreground-700 transition-colors hover:bg-background-100"
        >
          <i className="ri-arrow-up-line text-lg"></i>
        </button>
      ) : null}

      <CatalogProductModal
        item={selected}
        showPrices={config.show_prices}
        showWhatsapp={config.show_whatsapp}
        whatsappNumber={whatsappNumber}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}